import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const FAQPage = () => {
  const faqGeneral = [
    {
      id: 'general-1',
      question: "Qu'est-ce que CarMaintenance ?",
      answer: "CarMaintenance est une société qui propose un service d'abonnement innovant destiné aux particuliers et entreprises propriétaires de véhicules. Il permet de couvrir les frais fixes d'entretien (vidanges, révisions, contrôles) ainsi que de protéger contre les imprévus mécaniques (pannes, réparations importantes), le tout à travers une offre mensuelle simple, transparente et adaptée au profil de chaque véhicule. Vous trouverez les détails des couvertures de nos abonnements dans les conditions générales."
    },
    {
      id: 'general-2',
      question: "Pourquoi choisir un abonnement plutôt que payer mes entretiens et réparations au cas par cas ?",
      answer: "Parce qu'avec CarMaintenance, vous évitez les mauvaises surprises. Les entretiens et réparations coûtent souvent cher et arrivent toujours au mauvais moment. Grâce à un abonnement, vos dépenses auto deviennent prédictibles, régulières et maîtrisées : vous payez un montant fixe chaque mois, sans stress ni imprévus. De plus, vous bénéficiez d'un suivi régulier, de tarifs préférentiels et de la sérénité d'avoir toujours un professionnel à vos côtés en cas de panne."
    },
    {
      id: 'general-3',
      question: "En quoi CarMaintenance est différent d'un garage classique ?",
      answer: "Avec CarMaintenance, vous prenez le contrôle des coûts liés à votre véhicule. Vous payez un montant fixe chaque mois, qui couvre l'entretien, les diverses réparations ainsi que les pièces d'usure. Contrairement à un garage classique, vous savez d'avance ce que vous payez et pour quoi. Plus de doute, plus de stress, juste un service clair et fiable."
    },
    {
      id: 'general-4',
      question: "Comment s'abonner à CarMaintenance ?",
      answer: "Pour vous abonner à CarMaintenance, il vous suffit d'aller sur la page 'Nos Abonnements', de sélectionner la couverture qui vous convient. Vous serez redirigé vers un formulaire intuitif afin de renseigner vos données, celles de votre véhicule ainsi que vos données de paiement. Vous recevrez ensuite une confirmation par mail du contrat d'abonnement."
    },
    {
      id: 'general-5',
      question: "Est-ce que CarMaintenance remplace mon assurance automobile ?",
      answer: "Non. CarMaintenance ne remplace pas votre assurance automobile, mais elle couvre tous les frais liés à l'entretien et à la réparation de votre véhicule."
    },
    {
      id: 'general-6',
      question: "Puis-je choisir mon propre garage ?",
      answer: "Non, pour le moment CarMaintenance est disponible uniquement à Meyrin, où nous travaillons avec des garages partenaires de confiance. Nous nous efforçons cependant d'élargir progressivement notre réseau afin de proposer notre service partout en Suisse."
    }
  ];

  const faqSubscription = [
    {
      id: 'sub-1',
      question: "Mon véhicule est-il éligible à l'abonnement CarMaintenance ?",
      answer: "Lors de la souscription, le véhicule doit respecter les limites d'âge et de kilométrage suivantes :\n\n• Abonnement S : jusqu'à 20 ans et/ou 180 000 km\n• Abonnement M : jusqu'à 16 ans et/ou 150 000 km\n• Abonnement L : jusqu'à 16 ans et/ou 150 000 km\n\nCarMaintenance se réserve le droit de contrôler l'état du véhicule avant l'activation de l'abonnement."
    },
    {
      id: 'sub-2',
      question: "Comment souscrire à l'abonnement CarMaintenance ?",
      answer: "La souscription se fait directement en ligne : il vous suffit d'aller sur notre site, de choisir votre type d'abonnement (S,M,L), puis de renseigner les informations de votre véhicule (marque, modèle, année, kilométrage). Vous sélectionnez ensuite le montant de votre franchise par panne afin d'adapter l'abonnement à votre budget, puis vous complétez vos informations personnelles pour finaliser l'inscription. Une fois validée, votre couverture est active et vous profitez immédiatement des services CarMaintenance."
    },
    {
      id: 'sub-3',
      question: "Quelles prestations sont incluses dans l'abonnement ?",
      answer: "Tous les abonnements CarMaintenance incluent l'entretien courant du véhicule, réalisé conformément au plan d'entretien constructeur (vidanges, filtres, freins, contrôles de sécurité). Selon la formule choisie (S, M ou L) et le montant de franchise défini lors de la souscription, l'abonnement peut également prendre en charge différents types de pannes mécaniques ainsi que les différentes pièces d'usure. Pour toutes les interventions non couvertes, vous bénéficiez de tarifs préférentiels. Les détails précis de chaque formule et des prestations incluses sont indiqués dans les conditions générales de l'abonnement."
    },
    {
      id: 'sub-4',
      question: "Est-ce que je dois avancer les frais de réparations ?",
      answer: "Non, dans la majorité des cas vous n'avez rien à payer. CarMaintenance règle directement les garages partenaires. Les prises en charge varient selon le type d'abonnement choisi, puisque chaque formule couvre des entretiens et pannes différents. Une franchise par panne peut également s'appliquer, et son montant est librement défini par le client lors de la souscription, ce qui permet d'adapter le coût mensuel à ses besoins et à son budget. Enfin, si une panne ou une réparation n'est pas incluse dans votre formule, celle-ci vous sera proposée à un tarif préférentiel grâce à nos accords avec les garages partenaires."
    },
    {
      id: 'sub-5',
      question: "Dois-je renouveler mon abonnement annuellement ?",
      answer: "Non, la durée minimale de l'abonnement correspond à l'échéance initiale du contrat. Passé ce terme, l'abonnement est reconduit tacitement de mois en mois, sauf résiliation écrite par l'une des parties, avec un préavis de 30 jours."
    },
    {
      id: 'sub-6',
      question: "Puis-je transférer mon abonnement à une autre personne ou véhicule ?",
      answer: "Le transfert d'abonnement est possible sous certaines conditions. Par exemple, en cas de vente du véhicule, l'abonnement peut être soit transféré à l'acheteur de celui-ci, soit maintenu pour votre futur véhicule. Dans ce cas, en fonction du véhicule acheté, l'abonnement s'adaptera à celui-ci."
    }
  ];

  return (
    <div className="pt-20">
      <SEO
        title="FAQ — Toutes les réponses sur CarMaintenance"
        description="Questions fréquentes sur CarMaintenance : éligibilité, prestations couvertes, franchise, souscription en ligne, garages partenaires et plus encore."
        path="/faq"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [...faqGeneral, ...faqSubscription].map((q) => ({
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: { '@type': 'Answer', text: q.answer },
          })),
        }}
      />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 animate-in fade-in slide-in-from-bottom duration-700">
            FAQ
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Vous avez une question? Nous sommes là.
          </p>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-2">
            FAQ, solutions rapides et informations officielles sur chaque fonctionnalité.
          </p>
          <p className="text-gray-500 mt-4">
            Si vous ne trouvez pas votre question ici, consultez nos forums d'assistance.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-auto p-1 mb-12">
              <TabsTrigger value="general" className="text-base sm:text-lg py-3 data-[state=active]:shadow-sm">Questions générales</TabsTrigger>
              <TabsTrigger value="subscription" className="text-base sm:text-lg py-3 data-[state=active]:shadow-sm">Abonnement</TabsTrigger>
            </TabsList>

            <TabsContent value="general">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Questions générales :</h2>
              </div>
              <Accordion type="single" collapsible className="space-y-4">
                {faqGeneral.map((faq, index) => (
                  <AccordionItem
                    key={faq.id}
                    value={faq.id}
                    className="border border-gray-200 rounded-lg px-6 hover:border-[#52a675] transition-colors"
                  >
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#52a675] py-5">
                      <span className="mr-2">{index + 1}.</span> {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pb-5 leading-relaxed whitespace-pre-line">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>

            <TabsContent value="subscription">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Abonnement :</h2>
              </div>
              <Accordion type="single" collapsible className="space-y-4">
                {faqSubscription.map((faq, index) => (
                  <AccordionItem
                    key={faq.id}
                    value={faq.id}
                    className="border border-gray-200 rounded-lg px-6 hover:border-[#52a675] transition-colors"
                  >
                    <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#52a675] py-5">
                      <span className="mr-2">{index + 1}.</span> {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pb-5 leading-relaxed whitespace-pre-line">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>
          </Tabs>

          <div className="text-center mt-12">
            <Link to="/contact">
              <Button
                variant="outline"
                size="lg"
                className="border-[#52a675] text-[#52a675] hover:bg-[#52a675] hover:text-white"
                data-testid="faq-nous-contacter-btn"
              >
                Nous contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#e8f5ef] to-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Une question ? N'hésitez pas à nous contacter.
          </h2>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#52a675] hover:bg-[#3d8a5c] text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
              data-testid="faq-contactez-nous-btn"
            >
              Contactez-nous
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
